"use client";

import Img from '@/components/Img';
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useLocale } from "@/lib/locale-context";
import { track } from "@/lib/metrika";

type Lang = { code: "ru" | "en"; label: string; short: string; flag: string };

const LANGS: Lang[] = [
  { code: "ru", label: "Русский", short: "RU", flag: "/icons/flag-ru.svg" },
  { code: "en", label: "English", short: "EN", flag: "/icons/flag-en.svg" },
];

export default function LanguageSwitcher({ theme = "dark" }: { theme?: "dark" | "light" }) {
  const light = theme === "light";
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname() || "/";
  // Активный язык — из серверного контекста (надёжно при rewrite /en→/).
  const { locale: active } = useLocale();

  // Путь без префикса локали — устойчиво к обоим случаям (usePathname может
  // отдавать как /en/x, так и переписанный /x).
  const base = (pathname === "/en" || pathname.startsWith("/en/"))
    ? pathname.replace(/^\/en/, "") || "/"
    : pathname;

  function hrefFor(code: "ru" | "en"): string {
    if (code === "en") return base === "/" ? "/en" : `/en${base}`;
    return base;
  }

  function switchTo(code: "ru" | "en") {
    setOpen(false);
    if (code === active) return;
    track("lang_switch");
    // Жёсткая перезагрузка: корневой layout (LocaleProvider) не ре-рендерится
    // при router.push, поэтому меняем язык полной навигацией — сервер отдаёт
    // страницу с новым x-locale.
    window.location.assign(hrefFor(code));
  }

  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = LANGS.find((l) => l.code === active) ?? LANGS[0];

  return (
    <div ref={ref} className="relative hidden sm:block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={active === "en" ? "Language" : "Выбор языка"}
        className={`flex h-11 cursor-pointer items-center gap-1 rounded-xl px-3 text-[16px] leading-6 transition ${light ? "bg-[#EAF0FB] text-text hover:bg-[#DCE6F8]" : "bg-white/20 text-text-on-dark hover:bg-white/30"}`}
      >
        <span className="size-6">
          <Img src={current.flag} alt="" width={24} height={24} className="size-6" />
        </span>
        <span>{current.short}</span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
          className={`size-5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        >
          <path d="M6 9.5L12 15.5L18 9.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+8px)] z-40 w-full overflow-hidden rounded-xl bg-surface py-1 shadow-[0_12px_32px_rgba(20,29,69,0.18)] ring-1 ring-border"
        >
          {LANGS.map((l) => {
            const selected = l.code === active;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => switchTo(l.code)}
                  className={`flex h-11 w-full cursor-pointer items-center gap-1 px-3 text-[16px] leading-6 transition hover:bg-bg ${
                    selected ? "font-medium text-primary" : "text-text"
                  }`}
                >
                  <span className="size-6 shrink-0">
                    <Img src={l.flag} alt="" width={24} height={24} className="size-6" />
                  </span>
                  <span>{l.short}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
